<header class="flex header qv-header">
  <!-- The document number is what the user is looking for, so it is the title once loaded; the type sits
       right under it instead of being one more field in the body. -->
  <div>
    @if (payable(); as p) {
      <span class="qv-header__caption">{{ 'ApDocument_QuickView_Title' | translateWF: 'Document detail' }}</span>
      <h2>{{ p.document.number || '—' }}</h2>
      @if (typeLabel) {
        <div class="qv-header__tags">
          <p-tag [value]="typeLabel" severity="secondary" />
        </div>
      }
    } @else {
      <h2>{{ 'ApDocument_QuickView_Title' | translateWF: 'Document detail' }}</h2>
    }
  </div>
  <p-button icon="pi pi-times" (click)="onClose()" />
</header>

<section class="content">
  <loading-overlay [isLoading]="loading()"></loading-overlay>

  @if (loading()) {
    <!-- the overlay carries the feedback; nothing to render underneath it yet -->
  } @else if (failed() || !payable()) {
    <p class="text-surface-500 p-4">
      {{ 'ApDocument_QuickView_NotFound' | translateWF: 'This document could not be loaded.' }}
    </p>
  } @else {
    @let doc = payable()!.document;

    <section class="qv-body">
      <!-- Identity: the AP-generated number and the provider's own fiscal number are different things and a
           user reconciling against a paper invoice needs the second one (#4788). -->
      <div class="qv-identity">
        <div>
          <span class="qv-label">{{ 'ApDocument_Provider' | translateWF: 'Provider' }}</span>
          <span class="qv-identity__provider">{{ doc.providerName || '—' }}</span>
          @if (doc.providerCode) {
            <span class="qv-sub">{{ doc.providerCode }}</span>
          }
        </div>
        <div class="qv-identity__fiscal">
          <span class="qv-label">{{ 'ApDocument_ProviderInvoiceNumber' | translateWF: 'Provider invoice #' }}</span>
          <span class="qv-value">{{ doc.fiscalNumber || '—' }}</span>
        </div>
      </div>

      <!-- Amounts. balanceDue is already net of paid, withheld and applied advances. Each amount also in
           functional currency, converted at the rate this document was booked with. -->
      <div class="qv-amounts">
        <div class="qv-amount">
          <span class="qv-label">{{ 'ApDocument_TotalAmount' | translateWF: 'Total' }}</span>
          <span class="qv-amount__value"
            >{{ doc.totalAmount | number: '1.2-2' }} <small>{{ currencyLabel }}</small></span
          >
          <span class="qv-sub tabular-nums">{{ functional(doc.totalAmount) | number: '1.2-2' }} {{ functionalLabel }}</span>
        </div>
        <div class="qv-amount">
          <span class="qv-label">{{ 'ApDocument_Withheld' | translateWF: 'Withheld' }}</span>
          <span class="qv-amount__value"
            >{{ doc.withheldAmount ?? 0 | number: '1.2-2' }} <small>{{ currencyLabel }}</small></span
          >
          <span class="qv-sub tabular-nums">{{ functional(doc.withheldAmount) | number: '1.2-2' }} {{ functionalLabel }}</span>
        </div>
        <div class="qv-amount qv-amount--highlight">
          <span class="qv-label">{{ 'ApDocument_BalanceDue' | translateWF: 'Balance due' }}</span>
          <span class="qv-amount__value"
            >{{ doc.balanceDue ?? 0 | number: '1.2-2' }} <small>{{ currencyLabel }}</small></span
          >
          <span class="qv-sub tabular-nums">{{ functional(doc.balanceDue) | number: '1.2-2' }} {{ functionalLabel }}</span>
        </div>
      </div>

      <div class="qv-facts">
        <div>
          <span class="qv-label">{{ 'ApDocument_DocumentDate' | translateWF: 'Document date' }}</span>
          <span class="qv-value">{{ doc.documentDate ? (doc.documentDate | date: 'dd/MM/yyyy') : '—' }}</span>
        </div>
        <div>
          <span class="qv-label">{{ 'ApDocument_DueDate' | translateWF: 'Due date' }}</span>
          <span class="qv-value">{{ doc.dueDate ? (doc.dueDate | date: 'dd/MM/yyyy') : '—' }}</span>
        </div>
        <!-- The AP control account this document posted to. Local vs international providers post to
             different accounts, so this is what identifies where a payment/remittance applies. -->
        <div class="qv-facts__wide">
          <span class="qv-label">{{ 'ApDocument_ApAccount' | translateWF: 'AP account' }}</span>
          <span class="qv-value">
            {{ doc.apAccountNumber || '—' }}
            @if (doc.apAccountName) {
              <span class="text-surface-500"> · {{ doc.apAccountName | localized }}</span>
            }
          </span>
        </div>
      </div>

      <!-- Related notes + withholding, resolving to the net payable. Reuses the shared resumen so this modal
           and the payment pickers can never disagree about what the net is. Advances are suppressed: this is
           a "what is this document" view, not a settlement decision. -->
      @if (hasNotes) {
        <div>
          <div class="qv-section-title">
            <i class="pi pi-sliders-h"></i>
            {{ 'ApDocument_QuickView_Adjustments' | translateWF: 'Adjustments' }}
          </div>
          <app-payable-document-summary [payable]="payable()!" [currencyLabel]="currencyLabel" [showAdvances]="false" />
        </div>
      }

      <div>
        <div class="qv-section-title">
          <i class="pi pi-list"></i>
          {{ 'ApDocument_LineItems' | translateWF: 'Lines' }} · {{ doc.lineItems.length }}
        </div>
        <!-- #7051 — grouped per PO like the AP document page, so the same item invoiced on two POs at different
             prices reads as two groups. Lines with no PO get a header only when PO groups sit beside them. -->
        @for (group of lineGroups(); track group.reference; let last = $last) {
          <div class="po-group">
            @if (group.reference || lineGroups().length > 1) {
              <div class="po-group__header">
                <span class="po-group__title">
                  @if (group.reference) {
                    <i class="pi pi-shopping-cart"></i>
                    <strong>{{ group.number || '—' }}</strong>
                  } @else {
                    <i class="pi pi-file"></i>
                    <strong>{{ 'ApDocument_Note_NoPurchaseOrder' | translateWF: 'No purchase order' }}</strong>
                  }
                  · {{ group.lines.length }} {{ 'ApDocument_LinesLower' | translateWF: 'lines' }}
                </span>
              </div>
            }
            <p-table [value]="group.lines" styleClass="w-full p-datatable-sm" [tableStyle]="{ 'table-layout': 'fixed' }">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width: 36%">{{ 'ApDocument_Line_Description' | translateWF: 'Description' }}</th>
                  <th class="text-right" style="width: 12%">{{ 'ApDocument_Line_Quantity' | translateWF: 'Qty' }}</th>
                  <th class="text-right" style="width: 17%">{{ 'ApDocument_Line_UnitPrice' | translateWF: 'Unit price' }}</th>
                  <th class="text-right" style="width: 18%">{{ 'ApDocument_Line_Total' | translateWF: 'Total' }}</th>
                  <th class="text-right" style="width: 17%">{{ functionalLabel }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-line>
                <tr>
                  <td>
                    {{ line.description || line.itemCode || '—' }}
                    @if (line.itemCode && line.description) {
                      <small class="text-surface-500 block">{{ line.itemCode }}</small>
                    }
                  </td>
                  <td class="text-right tabular-nums">{{ line.quantity | number: '1.2-2' }}</td>
                  <td class="text-right tabular-nums">{{ line.unitPrice | number: '1.2-2' }}</td>
                  <td class="text-right tabular-nums">{{ line.lineTotal | number: '1.2-2' }}</td>
                  <td class="text-right tabular-nums text-surface-500">{{ functional(line.lineTotal) | number: '1.2-2' }}</td>
                </tr>
              </ng-template>
              <!-- The document total closes the last group, aligned to the same columns. -->
              <ng-template pTemplate="footer">
                @if (last) {
                  <tr class="qv-lines-total">
                    <td colspan="3" class="text-right text-surface-500">{{ 'ApDocument_TotalAmount' | translateWF: 'Total' }}</td>
                    <td class="text-right tabular-nums">{{ doc.totalAmount | number: '1.2-2' }}</td>
                    <td class="text-right tabular-nums text-surface-500">{{ functional(doc.totalAmount) | number: '1.2-2' }}</td>
                  </tr>
                }
              </ng-template>
            </p-table>
          </div>
        } @empty {
          <div class="po-group__empty">{{ 'ApDocument_QuickView_NoLines' | translateWF: 'No lines.' }}</div>
        }
      </div>

      @if (doc.notes) {
        <div>
          <span class="qv-label">{{ 'ApDocument_Notes' | translateWF: 'Notes' }}</span>
          <span class="qv-value whitespace-pre-line">{{ doc.notes }}</span>
        </div>
      }
    </section>
  }
</section>

<footer class="main__actions">
  <p-button [label]="'Close' | translateWF: 'Close'" variant="outlined" (click)="onClose()" />
</footer>
